// Not actual index, but a landing page for the app

import logo from "@/assets/trinity_logo.png"
import { useState } from "react"
import { ColoredTop } from "../misc"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, UseFormRegister, FieldErrors } from "react-hook-form"
import { ContactFormState } from "@/interfaces/contactUs_type"
import { SendContactUsForm } from "@/api/contactUs"
import LoginModal from "./UI/Login"

const contactUsSchema = z.object({
    contact_us_email: z.string().email({ message: "Invalid email address" }),
    message: z.string().min(1, { message: "Message is required" }),
    recaptcha: z.string().min(1, { message: "Recaptcha is required" })
})

type ContactUsFormData = z.infer<typeof contactUsSchema>

export default function LandingPage() {
    const [isInputFocused, setIsInputFocused] = useState(false)

    const { 
        register,
        handleSubmit,
        setError,
        formState: { errors, isSubmitting, isSubmitSuccessful },
    } = useForm<ContactUsFormData>({
        resolver: zodResolver(contactUsSchema)
    })

    const onSubmit = async (data: ContactUsFormData) => {
        const contactUsData: ContactFormState & {recaptcha: string} = {
            email: data.contact_us_email,
            message: data.message,
            recaptcha: data.recaptcha
        }

        try {
            await SendContactUsForm(contactUsData)
        } catch (error) {
            setError("root", { message: "Failed to send message. Please try again." })
        }
    }

    return (
        <div className="h-screen bg-white dark:bg-gray-900 relative">
            {/* Colored top bar */}
            <ColoredTop />
            
            {/* Simple Login Button in top right - positioned absolutely */}
            <div className="absolute top-4 right-4 z-10">
                <LoginNavBar />
            </div>
            
            {/* Main Google-style centered content - truly centered */}
            <div className="h-full flex flex-col items-center justify-center px-6">
                
                {/* Logo - Google style positioning with gentle scaling animation */}
                <div className="mb-8">
                    <img 
                        src={logo} 
                        alt="Trinity MEP Logo" 
                        className={`w-80 h-auto mx-auto transition-all duration-300 ${
                            isInputFocused ? 'animate-gentle-scale' : ''
                        }`}
                    />
                </div>
                
                {/* Conditional content based on submission status */}
                {isSubmitSuccessful ? (
                    /* Success state - replaces the form */
                    <div className="text-center space-y-6 max-w-xl">
                        <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                            Thank you for reaching out. 
                        </p>
                        <p className="text-lg text-gray-600 dark:text-gray-300">
                            One of the principals will get back to you shortly.
                        </p>
                        <button 
                            onClick={() => window.location.reload()}
                            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg text-lg font-medium transition-colors duration-200 shadow-lg hover:shadow-xl"
                        >
                            Send Another Message
                        </button>
                    </div>
                ) : (
                    /* Form state - normal form */
                    <form onSubmit={handleSubmit(onSubmit)} className="w-full max-w-xl flex flex-col items-center">
                        {/* Search-style text area */}
                        <div className="w-full mb-8">
                            <TextFormContactUs onFocusChange={setIsInputFocused} register={register} errors={errors} />
                        </div>

                        {/* Google-style buttons */}
                        <div className="flex gap-4">
                            <button 
                                type="submit"
                                disabled={isSubmitting}
                                className="bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-800 px-6 py-3 rounded text-sm font-medium transition-colors duration-200 dark:bg-gray-800 dark:hover:bg-gray-700 dark:border-gray-600 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? 'Sending...' : 'Send Message'}    
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    )
}

export function LoginNavBar() {
    const [isLoginOpen, setIsLoginOpen] = useState(false)

    return (
        <>
        <button 
            onClick={() => setIsLoginOpen(true)}
            className="text-sm text-gray-700 hover:text-gray-900 px-4 py-2 rounded hover:bg-gray-100 transition-colors duration-200 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800"
        >
            Sign in
        </button>
        {isLoginOpen && (
            <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />
        )}
        </>
    )
}

export function TextFormContactUs({ 
    onFocusChange, 
    register, 
    errors 
}: { 
    onFocusChange: (isFocused: boolean) => void, 
    register: UseFormRegister<ContactUsFormData>,
    errors: FieldErrors<ContactUsFormData>
}) {
    const [showEmailField, setShowEmailField] = useState(false)

    const handleFocus = () => {
        onFocusChange(true)
    }

    const handleBlur = () => {
        onFocusChange(false)
    }

    return (
        <div className="w-full space-y-4">
            {/* Google-style search box */}
            <div className="relative">
                <textarea 
                    {...register("message", {
                        onChange: (e) => {
                            const value = e.target.value
                            // Show email field when user starts typing
                            if (value.trim().length > 0 && !showEmailField) {
                                setShowEmailField(true)
                            } else if (value.trim().length === 0) {
                                setShowEmailField(false)
                            }
                        }
                    })}
                    className={`w-full px-6 py-4 text-lg border rounded-full shadow-sm hover:shadow-md focus:shadow-lg focus:outline-none resize-none overflow-hidden transition-shadow duration-200 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 ${
                        errors.message ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="How can we help you today?"
                    rows={1}
                    style={{minHeight: '56px'}}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                />
                
                {/* Search icon on the right */}
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>
            </div>

            {/* Error message for message field */}
            {errors.message && (
                <p className="text-red-500 text-sm px-2">{errors.message.message}</p>
            )}

            {/* Email input field - appears when user starts typing */}
            {showEmailField && (
                <div className="space-y-2">
                    <div className="relative animate-slide-down">
                        <input 
                            {...register("contact_us_email")}
                            type="email"
                            className={`w-full px-6 py-4 text-lg border rounded-full shadow-sm hover:shadow-md focus:shadow-lg focus:outline-none transition-shadow duration-200 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 ${
                                errors.contact_us_email ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Your email address"
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />
                        
                        {/* Email icon on the right */}
                        <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                            </svg>
                        </div>
                    </div>

                    {/* Error message for email field */}
                    {errors.contact_us_email && (
                        <p className="text-red-500 text-sm px-2">{errors.contact_us_email.message}</p>
                    )}
                    {errors.root && (
                        <p className="text-red-500 text-sm px-2">{errors.root.message}</p>
                    )}
                </div>
            )}

            {/* Hidden recaptcha field for now - you can implement this later */}
            <input 
                {...register("recaptcha")}
                type="hidden"
                value="temp-recaptcha-value"
            />
        </div>
    )
}